/**
 * Fullstory Properties Utilities
 *
 * Helpers for implementing the universal-data-scoping-and-decoration skill,
 * including page properties, element properties, and user properties APIs.
 *
 * Follows the data hierarchy:
 * USER PROPERTIES → PAGE PROPERTIES → ELEMENT PROPERTIES → EVENTS
 */

import { useEffect } from 'react';
import { PROPERTY_TYPES, DATA_ATTRIBUTES, FS_CONFIG } from './config';

declare global {
  interface Window {
    FS: (method: string, args?: any) => void;
  }
}

/**
 * Property Schema Definition
 * Defines which data attributes to capture and their types
 */
export interface PropertySchema {
  [attributeName: string]: string | { type: string; name?: string };
}

/**
 * Property Values Object
 * Flat key-value pairs for FS('setProperties') API
 */
export interface PropertyValues {
  [key: string]: string | number | boolean | string[] | null;
}

/**
 * Create an element properties schema for data-fs-properties-schema attribute
 *
 * @example
 * const schema = createPropertySchema({
 *   'data-product-price': { type: 'real', name: 'price' },
 *   'data-product-id': { type: 'str', name: 'productId' }
 * });
 * // Used in HTML as: data-fs-properties-schema='{...schema}'
 */
export function createPropertySchema(
  schema: Record<string, { type: string; name?: string }>
): PropertySchema {
  const result: PropertySchema = {};
  
  for (const [attr, config] of Object.entries(schema)) {
    result[attr] = {
      type: config.type,
      ...(config.name && { name: config.name }),
    };
  }
  
  return result;
}

/**
 * Get element properties schema as JSON string
 * Ready to use in data-fs-properties-schema attribute
 */
export function getPropertySchemaJSON(schema: PropertySchema): string {
  return JSON.stringify(schema);
}

/**
 * Set page properties for current page context
 *
 * Used for capturing page-level data that should persist until URL changes.
 * Examples: pageName, searchTerm, filters, productId
 *
 * @example
 * setPageProperties({
 *   pageName: 'Product Detail',
 *   productId: 'PROD-123',
 *   price: 29.99,
 *   category: 'Electronics'
 * });
 */
export function setPageProperties(properties: PropertyValues): void {
  if (!FS_CONFIG.features.pageProperties) return;
  
  if (typeof window !== 'undefined' && window.FS) {
    window.FS('setProperties', {
      type: 'page',
      properties,
    });
  }
}

/**
 * Set user properties for session context
 *
 * Used for capturing user-level data that persists across sessions.
 * Examples: plan, role, company, isCustomer
 *
 * Note: For sensitive user identification (email, ID), use FS.identify() instead.
 *
 * @example
 * setUserProperties({
 *   plan: 'enterprise',
 *   role: 'marketing',
 *   company: 'Acme Corp',
 *   isCustomer: true
 * });
 */
export function setUserProperties(properties: PropertyValues): void {
  if (!FS_CONFIG.features.pageProperties) return;
  
  if (typeof window !== 'undefined' && window.FS) {
    window.FS('setProperties', {
      type: 'user',
      properties,
    });
  }
}

/**
 * React hook for setting page properties on a template's mount.
 *
 * Use in every page template that has a clear "subject" (blog post, case
 * study, event, plan, industry) — the template's content type and Contentful
 * id go to page-level scope per the universal-data-scoping skill, Rule 1
 * (capture data at the highest relevant scope).
 *
 * @example
 *   // src/templates/blogPost.js
 *   usePageProperties({
 *     pageType: 'blog-post',
 *     pageName: post.title,
 *     contentfulId: post.id,
 *     slug: post.slug,
 *     blogPostId: post.id,
 *     author: post.author?.name,
 *     publishedAt: post.publishedAt,
 *     category: post.category?.name,
 *   });
 */
export function usePageProperties(properties: PropertyValues): void {
  useEffect(() => {
    setPageProperties(properties);
    // Stringify for stable dep identity — page-properties calls should fire
    // when content actually changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(properties)]);
}

/**
 * Clear user properties and reset session
 * Used on logout or consent revocation
 */
export function clearUserProperties(): void {
  if (typeof window !== 'undefined' && window.FS) {
    window.FS('setProperties', {
      type: 'user',
      properties: {},
    });
  }
}

/**
 * Element properties schemas for the marketing site.
 *
 * Each entry is a preset for `data-fs-properties-schema`. Apply by reading
 * `getPropertySchemaJSON(ElementPropertySchemas.X)` and setting it as the
 * attribute value. The component then carries individual `data-*` attributes
 * matching the schema's keys, and FS captures them at element-property time.
 *
 * Names mirror Contentful content types. Add new schemas here when you
 * decorate a new content shape.
 *
 *   data-fs-properties-schema='{...schema...}'   <- this preset
 *   data-{prop}='{value}'                        <- emit one per schema key
 */
export const ElementPropertySchemas = {
  /**
   * Blog post — `PageBlogPost` (and `PageBlogPost2026`).
   * Used on the blog post template root and on blog-post cards in lists.
   */
  blogPost: createPropertySchema({
    'data-blog-id':           { type: PROPERTY_TYPES.STRING,  name: 'blogPostId' },
    'data-content':           { type: PROPERTY_TYPES.STRING,  name: 'title' },
    'data-blog-author':       { type: PROPERTY_TYPES.STRING,  name: 'author' },
    'data-blog-category':     { type: PROPERTY_TYPES.STRING,  name: 'category' },
    'data-blog-publish-date': { type: PROPERTY_TYPES.DATE,    name: 'publishedAt' },
    'data-read-time-minutes': { type: PROPERTY_TYPES.INTEGER, name: 'readTimeMinutes' },
  }),

  /**
   * Case study — `PageCaseStudy`. Card and template root.
   */
  caseStudy: createPropertySchema({
    'data-case-id':       { type: PROPERTY_TYPES.STRING, name: 'caseStudyId' },
    'data-content':       { type: PROPERTY_TYPES.STRING, name: 'title' },
    'data-industry':      { type: PROPERTY_TYPES.STRING, name: 'industry' },
    'data-company':       { type: PROPERTY_TYPES.STRING, name: 'company' },
    'data-headline-stat': { type: PROPERTY_TYPES.STRING, name: 'headlineStat' },
  }),

  /**
   * Customer quote — `PageCustomerQuote`.
   * Distinct from `testimonial` — quotes are content-managed in Contentful
   * and pull customer/role/company. Used in TabbedCustomerStories etc.
   */
  customerQuote: createPropertySchema({
    'data-quote-id':      { type: PROPERTY_TYPES.STRING, name: 'quoteId' },
    'data-content':       { type: PROPERTY_TYPES.STRING, name: 'customer' },
    'data-quote-role':    { type: PROPERTY_TYPES.STRING, name: 'role' },
    'data-quote-company': { type: PROPERTY_TYPES.STRING, name: 'company' },
    'data-quote-industry':{ type: PROPERTY_TYPES.STRING, name: 'industry' },
  }),

  /**
   * Plan card — `ContentfulComponentPlansTab`.
   * Use on each Tab panel on the /plans page (Tab.js).
   * Pricing lives in rich-text introCopy — not a discrete field, so no price attr.
   * data-plan-tier maps to accentColor (the visual tier signal from Contentful).
   */
  planCard: createPropertySchema({
    'data-plan-id':       { type: PROPERTY_TYPES.STRING, name: 'planId' },
    'data-content':       { type: PROPERTY_TYPES.STRING, name: 'planName' },
    'data-plan-tier':     { type: PROPERTY_TYPES.STRING, name: 'tier' },
    'data-plan-cta-text': { type: PROPERTY_TYPES.STRING, name: 'ctaText' },
  }),

  /**
   * Industry tile — filter links and industry-labelled cards.
   * data-industry-vertical was removed — no discrete field exists in Contentful.
   */
  industryTile: createPropertySchema({
    'data-industry-id':   { type: PROPERTY_TYPES.STRING, name: 'industryId' },
    'data-content':       { type: PROPERTY_TYPES.STRING, name: 'name' },
  }),

  /**
   * Solutions catalogue tile — SolutionsCatalogue/ListItem.js.
   * Used for both "By Industry" and "By Team" tiles on /solutions.
   * Distinct from industryTile because solution tiles carry section context
   * and a destination URL rather than industry taxonomy fields.
   */
  solutionsTile: createPropertySchema({
    'data-solution-id':      { type: PROPERTY_TYPES.STRING, name: 'solutionId' },
    'data-content':          { type: PROPERTY_TYPES.STRING, name: 'name' },
    'data-solution-section': { type: PROPERTY_TYPES.STRING, name: 'section' },
    'data-solution-url':     { type: PROPERTY_TYPES.STRING, name: 'url' },
  }),

  /**
   * Event card — `PageEvent`.
   * Use on event cards in the events hub and on the event template root.
   */
  eventCard: createPropertySchema({
    'data-event-id':       { type: PROPERTY_TYPES.STRING, name: 'eventId' },
    'data-content':        { type: PROPERTY_TYPES.STRING, name: 'name' },
    'data-event-type':     { type: PROPERTY_TYPES.STRING, name: 'type' },
    'data-event-date':     { type: PROPERTY_TYPES.DATE,   name: 'eventDate' },
    'data-event-location': { type: PROPERTY_TYPES.STRING, name: 'location' },
    'data-event-virtual':  { type: PROPERTY_TYPES.BOOLEAN,name: 'isVirtual' },
  }),

  /**
   * Walled / gated resource — `PageWalledResource`, `PageResource`.
   * Pairs with the gated-content cookie check (useCheckGatedCookie).
   */
  walledResource: createPropertySchema({
    'data-resource-id':    { type: PROPERTY_TYPES.STRING,  name: 'resourceId' },
    'data-content':        { type: PROPERTY_TYPES.STRING,  name: 'title' },
    'data-resource-type':  { type: PROPERTY_TYPES.STRING,  name: 'resourceType' },
    'data-resource-gated': { type: PROPERTY_TYPES.BOOLEAN, name: 'isGated' },
  }),

  /**
   * Resources-hub search result — replaces the generic `searchResult` preset.
   * Used on each result card in the resources hub search/filter UI.
   */
  resourceSearchResult: createPropertySchema({
    'data-resource-id':    { type: PROPERTY_TYPES.STRING,  name: 'resourceId' },
    'data-content':        { type: PROPERTY_TYPES.STRING,  name: 'title' },
    'data-resource-type':  { type: PROPERTY_TYPES.STRING,  name: 'resourceType' },
    'data-search-query':   { type: PROPERTY_TYPES.STRING,  name: 'query' },
    'data-result-rank':    { type: PROPERTY_TYPES.INTEGER, name: 'rank' },
  }),

  /**
   * CTA button context — applies to PrimaryLink/SecondaryLink decoration in
   * Common/Links.js as well as standalone Get-Demo / Sign-Up CTAs in heroes
   * and PageSections.
   */
  ctaButton: createPropertySchema({
    'data-cta-text':     { type: PROPERTY_TYPES.STRING,  name: 'ctaText' },
    'data-cta-type':     { type: PROPERTY_TYPES.STRING,  name: 'ctaType' },
    'data-cta-href':     { type: PROPERTY_TYPES.STRING,  name: 'ctaHref' },
    'data-section':      { type: PROPERTY_TYPES.STRING,  name: 'pageSection' },
    'data-position':     { type: PROPERTY_TYPES.INTEGER, name: 'position' },
  }),

  /**
   * Form field — matches what form-config/src/elements/ actually emits.
   * Applied by setPropertySchema() in form-config/utils/helpers.js on each
   * Marketo input, select, and textarea.
   * data-field-error is stamped dynamically by formEvents.js via MutationObserver.
   */
  formField: createPropertySchema({
    'data-field-name':     { type: PROPERTY_TYPES.STRING,  name: 'fieldName' },
    'data-field-type':     { type: PROPERTY_TYPES.STRING,  name: 'fieldType' },
    'data-field-value':    { type: PROPERTY_TYPES.STRING,  name: 'fieldValue' },
    'data-field-selected': { type: PROPERTY_TYPES.BOOLEAN, name: 'isSelected' },
    'data-field-required': { type: PROPERTY_TYPES.BOOLEAN, name: 'isRequired' },
  }),

  /**
   * Form root — matches what form-config/src/form.js emits on the <form> element.
   */
  formRoot: createPropertySchema({
    'data-form-name': { type: PROPERTY_TYPES.STRING, name: 'formName' },
  }),

  /**
   * Form button — matches what form-config/src/elements/buttons.js emits.
   */
  formButton: createPropertySchema({
    'data-button-type': { type: PROPERTY_TYPES.STRING, name: 'buttonType' },
    'data-button-name': { type: PROPERTY_TYPES.STRING, name: 'buttonName' },
  }),

  /**
   * Testimonial — distinct from `customerQuote`. Used for inline customer
   * testimonial cards (TestimonialCard, Reviews.js) where rating + review
   * date are present.
   */
  testimonial: createPropertySchema({
    'data-content':          { type: PROPERTY_TYPES.STRING, name: 'reviewerName' },
    'data-reviewer-company': { type: PROPERTY_TYPES.STRING, name: 'company' },
    'data-rating':           { type: PROPERTY_TYPES.REAL,   name: 'rating' },
    'data-review-date':      { type: PROPERTY_TYPES.DATE,   name: 'reviewDate' },
  }),

  /**
   * Navigation item — primary nav (desktop + mobile) and footer links.
   * data-nav-level is a string: "1" (top rail), "2" (sub-items), "callout".
   * data-nav-parent is the parent division name; empty string for level 1.
   */
  navigationItem: createPropertySchema({
    'data-nav-label':  { type: PROPERTY_TYPES.STRING, name: 'label' },
    'data-nav-href':   { type: PROPERTY_TYPES.STRING, name: 'href' },
    'data-nav-level':  { type: PROPERTY_TYPES.STRING, name: 'level' },
    'data-nav-parent': { type: PROPERTY_TYPES.STRING, name: 'parentId' },
  }),

  /**
   * Generic content element — use when data-content is set but no more specific
   * schema applies (e.g. accordion items, simple cards, large cards, heroes).
   * data-content maps to the "content" property (the element's primary text/name).
   */
  contentItem: createPropertySchema({
    'data-content': { type: PROPERTY_TYPES.STRING, name: 'content' },
  }),

  /**
   * Field Notes post card — `PageFieldNotesPost` cards on the /field-notes landing page.
   * data-field-notes-topics is a comma-separated string (schema fields are scalar).
   */
  fieldNotesCard: createPropertySchema({
    'data-field-notes-id':      { type: PROPERTY_TYPES.STRING,  name: 'fieldNotesId' },
    'data-content':             { type: PROPERTY_TYPES.STRING,  name: 'title' },
    'data-field-notes-author':  { type: PROPERTY_TYPES.STRING,  name: 'author' },
    'data-field-notes-topics':  { type: PROPERTY_TYPES.STRING,  name: 'topics' },
    'data-field-notes-lab':     { type: PROPERTY_TYPES.BOOLEAN, name: 'isLab' },
    'data-field-notes-date':    { type: PROPERTY_TYPES.DATE,    name: 'publishedAt' },
  }),

  /**
   * Quiz answer button — maturity-matrix quiz (Quiz/Matrix.js).
   * data-content is the selected answer option; data-question is the question text.
   */
  quizAnswer: createPropertySchema({
    'data-content':  { type: PROPERTY_TYPES.STRING, name: 'answer' },
    'data-question': { type: PROPERTY_TYPES.STRING, name: 'question' },
  }),
};

/**
 * Validate that a schema serialises and survives a JSON round-trip.
 * Use this in development to catch malformed schema literals before they
 * land in DOM attributes.
 */
export function validatePropertySchemaJSON(schema: PropertySchema): {
  valid: boolean;
  json?: string;
  error?: string;
} {
  try {
    const json = getPropertySchemaJSON(schema);
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) {
      return { valid: false, error: 'Parsed schema is not an object' };
    }
    return { valid: true, json };
  } catch (e: any) {
    return { valid: false, error: e?.message || String(e) };
  }
}

/**
 * Get element properties from an element's data attributes
 * Inverse of setting properties - extracts captured data
 */
export function getElementProperties(element: HTMLElement): PropertyValues {
  const properties: PropertyValues = {};
  
  // Extract all data-* attributes except framework attributes
  Array.from(element.attributes).forEach(attr => {
    if (
      attr.name.startsWith('data-') &&
      !attr.name.startsWith('data-fs-') &&
      !attr.name.startsWith('data-component') &&
      !attr.name.startsWith('data-id') &&
      !attr.name.startsWith('data-section') &&
      !attr.name.startsWith('data-position') &&
      !attr.name.startsWith('data-state') &&
      !attr.name.startsWith('data-testid')
    ) {
      properties[attr.name] = attr.value;
    }
  });
  
  return properties;
}

/**
 * Validate property schema matches element attributes
 * Useful for development/debugging
 */
export function validatePropertySchema(
  element: HTMLElement,
  schema: PropertySchema
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  for (const [attrName] of Object.entries(schema)) {
    if (!element.hasAttribute(attrName)) {
      errors.push(`Missing attribute: ${attrName}`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Log element properties for debugging
 */
export function logElementProperties(element: HTMLElement): void {
  if (FS_CONFIG.isProduction) return;
  
  const schema = element.getAttribute(DATA_ATTRIBUTES.PROPERTIES_SCHEMA);
  const properties = getElementProperties(element);
  
  console.log('[FS Properties]', {
    element: element.getAttribute(DATA_ATTRIBUTES.ELEMENT),
    schema: schema ? JSON.parse(schema) : null,
    properties,
  });
}
